import { useCallback, useRef, useState } from 'react';

import {
    AIRPORT_CATEGORY_MARKER,
    AIRPORT_CODE_PATTERN,
    DEBOUNCE_MS,
    FORWARD_GEOCODE_COUNTRY,
    FORWARD_GEOCODE_LIMIT,
    FORWARD_GEOCODE_TYPES,
    MAPBOX_FORWARD_GEOCODE_PATH,
    MAPBOX_GEOCODING_HOST
} from './constants';
import type {
    ForwardFeature,
    ForwardFeatureKind,
    MapboxForwardFeature,
    MapboxForwardGeocodingResponse,
    UseForwardGeocodeReturn
} from './types';

// Detect airports either by explicit Mapbox category or by the "(AUS)" style
// IATA/ICAO code embedded in feature.text — both appear in practice.
const resolveKind = (feature: MapboxForwardFeature): ForwardFeatureKind => {
    const category = feature.properties?.category ?? '';
    const primary = feature.text ?? '';

    if (category.includes(AIRPORT_CATEGORY_MARKER) || AIRPORT_CODE_PATTERN.test(primary)) {
        return 'airport';
    }

    return 'place';
};

// `place_name` = full ", "-joined label; prefer "everything after the primary"
// for the secondary line. If that leaves an empty string (rare — primary ==
// place_name), fall back to the Mapbox `context` chain (next-up geographic
// parents: neighborhood / place / region).
const resolveSecondary = (feature: MapboxForwardFeature): string => {
    const primary = feature.text ?? '';
    const placeName = feature.place_name ?? '';
    const strippedPrefix =
        primary.length > 0 && placeName.startsWith(primary)
            ? placeName.slice(primary.length)
            : placeName;
    const trimmed = strippedPrefix.replace(/^,\s*/, '').trim();

    if (trimmed.length > 0) {
        return trimmed;
    }

    const context = feature.context ?? [];

    return context
        .map(item => item.text ?? '')
        .filter(text => text.length > 0)
        .join(', ');
};

export const parseForwardGeocode = (response: MapboxForwardGeocodingResponse): ForwardFeature[] => {
    const features = response.features ?? [];
    const parsed: ForwardFeature[] = [];

    features.forEach((feature, index) => {
        const { center } = feature;

        if (!center || center.length !== 2) {
            return;
        }

        const [lng, lat] = center;

        parsed.push({
            id: feature.id ?? `forward-${index}`,
            kind: resolveKind(feature),
            lat,
            lng,
            primary: feature.text ?? feature.place_name ?? '',
            secondary: resolveSecondary(feature)
        });
    });

    return parsed;
};

const buildUrl = (query: string, token: string): string => {
    const encodedQuery = encodeURIComponent(query);
    const params = new URLSearchParams({
        access_token: token,
        autocomplete: 'true',
        country: FORWARD_GEOCODE_COUNTRY,
        limit: String(FORWARD_GEOCODE_LIMIT),
        types: FORWARD_GEOCODE_TYPES
    });

    return `${MAPBOX_GEOCODING_HOST}${MAPBOX_FORWARD_GEOCODE_PATH}/${encodedQuery}.json?${params.toString()}`;
};

const useForwardGeocode = (): UseForwardGeocodeReturn => {
    const [results, setResults] = useState<ForwardFeature[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchResults = useCallback(async (query: string): Promise<void> => {
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        if (abortControllerRef.current !== null) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        const trimmed = query.trim();

        if (trimmed.length === 0) {
            setResults([]);
            setIsLoading(false);

            return;
        }

        const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;

        if (!token) {
            setResults([]);

            return;
        }

        await new Promise<void>(resolve => {
            debounceTimerRef.current = setTimeout(() => resolve(), DEBOUNCE_MS);
        });

        const controller = new AbortController();

        abortControllerRef.current = controller;

        try {
            setIsLoading(true);

            const response = await fetch(buildUrl(trimmed, token), { signal: controller.signal });

            if (!response.ok) {
                setResults([]);

                return;
            }

            const body = (await response.json()) as MapboxForwardGeocodingResponse;

            setResults(parseForwardGeocode(body));
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }

            console.error('Forward geocode failed', error);
            setResults([]);
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }

            setIsLoading(false);
        }
    }, []);

    return { fetchResults, isLoading, results };
};

export default useForwardGeocode;
